import * as THREE from 'three';

export interface TreeInstanceData {
  position: THREE.Vector3;
  scale?: number;
  rotationY?: number;
}

export class InstancedPineForest {
  public mesh: THREE.InstancedMesh;

  constructor(instances: TreeInstanceData[]) {
    // Combine trunk cylinder + 2 tiered cones into a single geometry to render in 1 draw call
    const trunkGeo = new THREE.CylinderGeometry(0.2, 0.35, 1.2, 5);
    trunkGeo.translate(0, 0.6, 0);

    const foliage1Geo = new THREE.ConeGeometry(1.6, 2.2, 5);
    foliage1Geo.translate(0, 2.0, 0);

    const foliage2Geo = new THREE.ConeGeometry(1.2, 1.8, 5);
    foliage2Geo.translate(0, 3.0, 0);

    // Merge via BufferGeometryUtils or standard BufferGeometry
    const combinedGeo = this.mergeGeometries([trunkGeo, foliage1Geo, foliage2Geo]);

    const mat = new THREE.MeshStandardMaterial({
      color: 0x1e3a29,
      roughness: 0.85,
      metalness: 0.05,
      flatShading: true,
    });

    this.mesh = new THREE.InstancedMesh(combinedGeo, mat, instances.length);
    this.mesh.castShadow = true;
    this.mesh.receiveShadow = true;

    const dummy = new THREE.Object3D();
    for (let i = 0; i < instances.length; i++) {
      const inst = instances[i];
      dummy.position.copy(inst.position);
      dummy.rotation.y = inst.rotationY ?? Math.random() * Math.PI * 2;
      dummy.scale.setScalar(inst.scale ?? (0.8 + Math.random() * 0.5));
      dummy.updateMatrix();
      this.mesh.setMatrixAt(i, dummy.matrix);
    }

    this.mesh.instanceMatrix.needsUpdate = true;
  }

  private mergeGeometries(geos: THREE.BufferGeometry[]): THREE.BufferGeometry {
    // Simple position merger
    let totalPosCount = 0;
    let totalIndexCount = 0;

    for (const g of geos) {
      totalPosCount += g.attributes.position.count;
      if (g.index) totalIndexCount += g.index.count;
    }

    const mergedPos = new Float32Array(totalPosCount * 3);
    const mergedNorm = new Float32Array(totalPosCount * 3);
    const mergedIndices: number[] = [];

    let posOffset = 0;
    let vertexOffset = 0;

    for (const g of geos) {
      const pos = g.attributes.position.array;
      mergedPos.set(pos, posOffset * 3);

      if (g.attributes.normal) {
        mergedNorm.set(g.attributes.normal.array, posOffset * 3);
      }

      if (g.index) {
        for (let i = 0; i < g.index.count; i++) {
          mergedIndices.push(g.index.getX(i) + vertexOffset);
        }
      }

      vertexOffset += g.attributes.position.count;
      posOffset += g.attributes.position.count;
    }

    const merged = new THREE.BufferGeometry();
    merged.setAttribute('position', new THREE.BufferAttribute(mergedPos, 3));
    merged.setAttribute('normal', new THREE.BufferAttribute(mergedNorm, 3));
    merged.setIndex(mergedIndices);
    merged.computeVertexNormals();

    return merged;
  }

  public dispose(): void {
    this.mesh.geometry.dispose();
    if (this.mesh.material instanceof THREE.Material) {
      this.mesh.material.dispose();
    }
  }
}
